import { useLayoutEffect } from "react";
import { type NavigationType, useLocation, useNavigationType } from "react-router-dom";

/**
 * Cascade d'entrée : elle accueille, elle ne se répète pas (DS §6.18).
 *
 * `.home-stage > *` anime `lueur-rise` depuis `opacity: 0` ; PageShell pose
 * `.home-stage` sur presque tous les écrans, donc la cascade rejoue à chaque
 * montage — à chaque « Retour », l'écran de destination restait blanc ~0,5 s.
 *
 * Le repère vit sur <html> (comme `is-native`, `lib/platform/native-class.ts`)
 * et JAMAIS dans un état React : un remontage de PageShell, de l'App ou le
 * double montage de StrictMode ne doivent pas le réinitialiser. Un vrai
 * rechargement de page repart d'un document neuf — donc la cascade rejoue,
 * ce qui est exactement ce qu'on veut.
 */
export const CLASSE_NAV_INTERNE = "nav-interne";

/**
 * Marque le document dès qu'une navigation VOULUE (`PUSH`/`POP`) succède au
 * premier affichage. Le marquage ne se retire jamais : revenir à l'entrée
 * initiale reste une navigation interne.
 *
 * Les `REPLACE` sont exclus : ce sont les redirections de démarrage
 * (`<Navigate replace />` de ProtectedRoute, deep-links `?captureId=`, retour
 * d'AuthCallback). L'écran vers lequel elles redirigent est le PREMIER écran
 * de l'app — sa cascade doit jouer.
 */
export function marquerNavigation(
  cle: string,
  type: NavigationType,
  racine: HTMLElement = document.documentElement,
): void {
  const precedente = racine.dataset.navCle;
  // Même entrée d'historique revue (re-render, double montage StrictMode) :
  // ce n'est pas une navigation.
  if (precedente === cle) return;
  racine.dataset.navCle = cle;
  if (precedente === undefined) return; // premier affichage
  if (type === "REPLACE") return; // redirection de démarrage
  racine.classList.add(CLASSE_NAV_INTERNE);
}

/**
 * Sentinelle montée une fois dans les routes (à côté de `useDeepLinkListener`).
 * `useLayoutEffect` et non `useEffect` : le marquage doit être posé dans le
 * même commit que l'écran de destination, AVANT que le navigateur ne peigne —
 * sinon la cascade démarre puis s'annule, et le blanc reste visible.
 */
export function useCascadeEntree(): void {
  const { key } = useLocation();
  const type = useNavigationType();
  useLayoutEffect(() => {
    marquerNavigation(key, type);
  }, [key, type]);
}
